export type FormErrors<T> = Partial<Record<keyof T, string>>;

// ─── Rule Types ───────────────────────────────────────────────────────────────
type ValidationRule = (value: string) => string;
export type ValidationFileRule = (
  value: string | File | null | undefined,
) => string;
// ─── Built-in Rule Factories ──────────────────────────────────────────────────

export const indianPhone =
  (message = "Must be a valid 10-digit Indian number!"): ValidationRule =>
  (value) =>
    value && !/^[6-9]\d{9}$/.test(value.trim()) ? message : "";
// value && !/^[6-9]\d{10}$/.test(value.trim()) ? message : "";

export const validEmail =
  (message = "Must be a valid email address!"): ValidationRule =>
  (value) =>
    value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? message : "";

export const positiveNumber =
  (message = "Must be a positive number!"): ValidationRule =>
  (value) =>
    value && (isNaN(Number(value)) || Number(value) <= 0) ? message : "";

export const yyyymmdd =
  (message = "Must be in YYYY-MM-DD format!"): ValidationRule =>
  (value) =>
    value && !/^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? message : "";

export const mmyy =
  (message = "Must be in MM/YY format!"): ValidationRule =>
  (value) =>
    value && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(value.trim()) ? message : "";

export const required =
  (message = "This field is required."): ValidationRule =>
  (value) =>
    !value.trim() ? message : "";

export const requiredIf =
  <T>(
    condition: (data: T) => boolean,
    message = "This field is required.",
  ): ValidationRule =>
  (value, data?: T) =>
    condition(data as T) && !value?.trim() ? message : "";

export const requiredSelect =
  (message = "Please select an option."): ValidationRule =>
  (value) =>
    !value ? message : "";

export const requiredFile =
  (message = "Signature is required."): ValidationFileRule =>
  (value) => {
    console.log("VALIDATOR VALUE:", value);

    if (!value) {
      return message;
    }
    if (value instanceof File) {
      return "";
    }

    return "Please upload a valid file.";
  };
// value instanceof File ? "" : message;

// easily extendable — add more rules as needed
export const minLength =
  (min: number, message?: string): ValidationRule =>
  (value) =>
    value.trim().length < min
      ? (message ?? `Minimum ${min} characters required.`)
      : "";

export const maxLength =
  (max: number, message?: string): ValidationRule =>
  (value) =>
    value.trim().length > max
      ? (message ?? `Maximum ${max} characters allowed.`)
      : "";

export const pattern =
  (regex: RegExp, message: string): ValidationRule =>
  (value) =>
    !regex.test(value) ? message : "";

// ─── Core: validate a single field against its rules ─────────────────────────
export const validateField = (
  rules: ValidationRule[],
  value: unknown,
  // value: string,
): string => {
  for (const rule of rules) {
    const error = rule(value as string);
    if (error) return error; // return first error hit
  }
  return "";
};

export type ValidationSchema<T> = Partial<Record<keyof T, ValidationRule[]>>;

// ─── Core: validate entire form ───────────────────────────────────────────────
export const validateForm = <T extends Record<string, unknown>>(
  formData: T,
  schema: Partial<Record<keyof T, ValidationRule[]>>,
): Partial<Record<keyof T, string>> => {
  const errors: Partial<Record<keyof T, string>> = {};

  (Object?.keys(schema) as (keyof T)[])?.forEach((key) => {
    const rules = schema[key] ?? [];
    const error = validateField(rules, String(formData[key] ?? ""));
    if (error) errors[key] = error;
  });

  return errors;
};
